import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { compose, cloneDeep, isObject, isEqual } from '@elliemae/ds-utilities/utils';
import { useRef, useState, useMemo } from 'react';
import { v4 } from 'uuid';
import * as resolve from 'table-resolver';
import useDerivedStateFromProps from '@elliemae/ds-utilities/hooks/useDerivedStateFromProps';
import usePrevious from '@elliemae/ds-utilities/hooks/usePrevious';
import { setRef } from '@elliemae/ds-utilities/system';
import initColumnDefinition from './initColumnDefinition.js';
import { applyDecorators, decorateColumns, reducePropsGetter } from '../createDataInstance/utils.js';
import VolatileRowsListener from './VolatileRowsListener.js';
import { isInternetExplorer } from '../utils.js';
import '@babel/runtime/helpers/esm/toConsumableArray';
import '@babel/runtime/helpers/esm/classCallCheck';
import '@babel/runtime/helpers/esm/createClass';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var IS_INTERNET_EXPLORER = isInternetExplorer(); // TODO: Reuse createDataInstance for the grid the same
//  way as the list does ex: packages/ds-shared/src/useDataList/useDataList.js
// eslint-disable-next-line max-statements

function useDataGrid(_ref) {
  var uuidProp = _ref.uuid,
      pluginsFromProps = _ref.plugins,
      rows = _ref.rows,
      columns = _ref.columns,
      _ref$renderers = _ref.renderers,
      renderers = _ref$renderers === void 0 ? {} : _ref$renderers,
      props = _objectWithoutProperties(_ref, ["uuid", "plugins", "rows", "columns", "renderers"]);

  var plugins = pluginsFromProps;
  var instance = useRef();
  var tableRef = useRef();
  var headerRef = useRef();
  var bodyRef = useRef();
  var bodyInnerRef = useRef();

  var _useState = useState(uuidProp || function () {
    return v4();
  }),
      _useState2 = _slicedToArray(_useState, 1),
      uuid = _useState2[0];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      hasScroll = _useState4[0],
      setHasScroll = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isRowDragging = _useState6[0],
      setIsRowDragging = _useState6[1];

  var isScrolling = useRef();
  var shouldRefocus = useRef(); // todo: we probably need a plugin for focus related stuff

  var _useState7 = useState(function () {
    return new VolatileRowsListener();
  }),
      _useState8 = _slicedToArray(_useState7, 1),
      RowsObserver = _useState8[0]; // todo move this with rows composition


  var _useDerivedStateFromP = useDerivedStateFromProps(rows, {
    onUpdate: resolve.resolve({
      columns: columns,
      method: function method(extra) {
        return compose(resolve.byFunction('valueTransformation')(extra), resolve.nested(extra));
      }
    }),
    updateOnStateChange: true
  }),
      _useDerivedStateFromP2 = _slicedToArray(_useDerivedStateFromP, 2),
      originalRows = _useDerivedStateFromP2[0],
      updateRows = _useDerivedStateFromP2[1];

  instance.current = {
    decoratedRenderers: cloneDeep(renderers),
    decoratedColumns: columns,
    columns: columns,
    rows: rows,
    composedRows: originalRows,
    getRowProps: [],
    getHeaderRowProps: [],
    getBodyProps: [],
    getTableProps: [],
    effects: [],
    uuid: uuid,
    props: props,
    refs: {
      table: tableRef,
      body: bodyRef,
      header: headerRef,
      innerBody: bodyInnerRef
    },
    state: {
      hasScroll: hasScroll
    },
    actions: {
      updateRows: updateRows
    },
    updateRows: updateRows,
    // eslint-disable-next-line no-return-assign
    enableEvents: function enableEvents() {
      return document.body.style.pointerEvents = IS_INTERNET_EXPLORER ? '' : null;
    },
    // eslint-disable-next-line no-return-assign
    disableEvents: function disableEvents() {
      return document.body.style.pointerEvents = 'none';
    },
    observeRows: function observeRows(observer) {
      return RowsObserver.observe(observer);
    },
    hotKeys: {},
    shouldRefocus: shouldRefocus,
    isScrolling: isScrolling,
    setHasScroll: setHasScroll,
    setIsRowDragging: setIsRowDragging,
    isRowDragging: isRowDragging,
    plugins: plugins
  };

  instance.current.getState = function () {
    return instance.current.state;
  };

  instance.current.getInstance = function () {
    return instance.current;
  };

  var defaultDecorators = {
    decorateGrid: [],
    registerStateHook: [],
    registerHotKeys: [],
    decorateColumn: [],
    decorateColumns: [function (cols) {
      return cols.map(function (col) {
        return initColumnDefinition({
          useTextTruncation: col.useTextTruncation,
          wrapText: props.wrapText,
          normalize: props.normalizeDataKeys
        })(col);
      });
    }],
    decorateRenderers: [],
    getRowProps: [],
    getHeaderRowProps: [],
    getBodyProps: [],
    getTableProps: [],
    getHeaderProps: [],
    composeRows: []
  };
  var decorators = useMemo(function () {
    var nextDecorators = defaultDecorators; // eslint-disable-next-line no-return-assign

    plugins.forEach(function (plugin) {
      return nextDecorators = plugin(nextDecorators);
    });
    return nextDecorators;
  }, [plugins]);
  instance.current = decorators.decorateGrid.reduce(function (nextInstance, decorator) {
    return _objectSpread(_objectSpread({}, nextInstance), decorator(instance.current));
  }, instance.current);
  instance.current = decorators.registerStateHook.reduce(function (nextInstance, decorator) {
    var _decorator = decorator(instance.current),
        state = _decorator.state,
        actions = _decorator.actions;

    return _objectSpread(_objectSpread({}, nextInstance), {}, {
      state: _objectSpread(_objectSpread({}, nextInstance.state), state),
      actions: _objectSpread(_objectSpread({}, nextInstance.actions), actions)
    });
  }, instance.current);

  var reduceHotKey = function reduceHotKey(nextInstance, _ref2) {
    var key = _ref2.key,
        handler = _ref2.handler,
        options = _ref2.options;
    return _objectSpread(_objectSpread({}, nextInstance), {}, {
      hotKeys: _objectSpread(_objectSpread({}, nextInstance.hotKeys), {}, _defineProperty({}, key, {
        handler: handler,
        options: options
      }))
    });
  };

  instance.current = decorators.registerHotKeys.reduce(function (nextInstance, decorator) {
    var hotKeys = decorator(nextInstance);

    if (Array.isArray(hotKeys)) {
      return hotKeys.reduce(reduceHotKey, nextInstance);
    }

    return reduceHotKey(nextInstance, hotKeys);
  }, instance.current);
  instance.current.decoratedRenderers = applyDecorators(instance.current.decoratedRenderers, decorators.decorateRenderers, instance.current);
  instance.current.decoratedColumns = useMemo(function () {
    var visibleColumns = cloneDeep(columns).filter(function (column) {
      return typeof column.visible === 'undefined' || column.visible;
    });
    return visibleColumns.length ? decorateColumns(visibleColumns, decorators, instance.current) : [];
  }, [columns]);
  instance.current.getBodyProps = reducePropsGetter(decorators.getBodyProps, instance.current);
  instance.current.getHeaderProps = reducePropsGetter(decorators.getHeaderProps, instance.current);
  instance.current.getRowProps = reducePropsGetter(decorators.getRowProps, instance.current);
  instance.current.getHeaderRowProps = reducePropsGetter(decorators.getHeaderRowProps, instance.current);
  instance.current.getTableProps = reducePropsGetter(decorators.getTableProps, instance.current);
  instance.current.composedRows = decorators.composeRows.reduce(function (nextRows, getter) {
    return getter(nextRows, instance.current);
  }, instance.current.composedRows);
  RowsObserver.notify(instance.current.composedRows);
  var currentShouldRefocus = instance.current.shouldRefocus.current;
  var prevState = usePrevious(instance.current.state);

  if (isObject(currentShouldRefocus) && currentShouldRefocus.forced) {
    instance.current.shouldRefocus.current = currentShouldRefocus.value;
  } else {
    instance.current.shouldRefocus.current = !isEqual(prevState, instance.current.state);
  }

  if (props.instanceRef) setRef(props.instanceRef, instance.current);
  return _objectSpread(_objectSpread({}, instance.current), {}, {
    decorators: decorators
  });
}

export default useDataGrid;
//# sourceMappingURL=useDataGrid.js.map
